import { AxiosError, type AxiosInstance, HttpStatusCode } from "axios";
import * as Sentry from "@sentry/react";
import type {
  ITokenExchangeRequest,
  ITokenExchangeResponse,
} from "@/types/jwt";
import {
  tokenExchangeCoreSchema,
  tokenExchangeResponseSchema,
} from "@/features/salesforceIntegration/schemas/salesforceContext";

interface TokenExchangeAPIResponse {
  data?: ITokenExchangeResponse;
  status: number;
  error?: string;
  /** バックエンドが返すエラーコード（許可IP外の判別に使う） */
  errorCode?: string;
}

interface ITokenExchangeErrorResponse {
  code?: string;
}

/**
 * トークン交換レスポンスの契約を検証する。
 * OpenAPIからの型生成が無いため、実行時検証で契約の崩れを検知しSentryへ送る。
 *
 * `salesforceContext` の契約違反ではログインを止めない。
 * フロントエンドとバックエンドは別々にデプロイされるため、項目の追加前後で
 * 契約が揃わない期間が生じる。SSO自体を落とすと利用者が復旧できない。
 */
const validateTokenExchangeResponseContract = (
  payload: unknown
): ITokenExchangeResponse => {
  const parsed = tokenExchangeResponseSchema.safeParse(payload);
  if (parsed.success) {
    return parsed.data;
  }

  Sentry.captureException(new Error("トークン交換レスポンスの契約違反"), {
    extra: { issues: parsed.error.issues },
  });

  const core = tokenExchangeCoreSchema.safeParse(payload);
  if (!core.success) {
    throw new Error("トークン交換レスポンスにトークンが含まれていません");
  }

  return { ...core.data, salesforceContext: null };
};

/**
 * JWT認可コードをアクセストークンとリフレッシュトークンに交換するAPI
 * @param {AxiosInstance} apiClient - Axiosインスタンス
 * @param {string} code - 認可コード
 * @returns {Promise<TokenExchangeAPIResponse>}
 */
export const tokenExchangeAPI = async (
  apiClient: AxiosInstance,
  code: string
): Promise<TokenExchangeAPIResponse> => {
  try {
    const tokenRequest: ITokenExchangeRequest = {
      code,
    };

    const response = await apiClient.post<ITokenExchangeResponse>(
      "sso/token/",
      tokenRequest
    );

    return {
      data: validateTokenExchangeResponseContract(response.data),
      status: response.status,
    };
  } catch (error) {
    if (error instanceof AxiosError && error.response != null) {
      const errorResponse = error.response.data as
        | ITokenExchangeErrorResponse
        | undefined;

      return {
        status: error.response.status ?? HttpStatusCode.InternalServerError,
        error: error.message,
        errorCode: errorResponse?.code,
      };
    }

    return {
      status: HttpStatusCode.InternalServerError,
      error: "予期しないエラーが発生しました",
    };
  }
};
